// composables/useCurrency.js
import { useCurrencyInput } from 'vue-currency-input';

/**
 * Global currency configuration for EGP
 * Using ValueScaling.precision to handle cents automatically
 */
export const EGP_CURRENCY_OPTIONS = {
  locale: 'ar-AE',
  currency: 'EGP',
  currencyDisplay: 'symbol',
  precision: 2,
  valueScaling: 'precision', // Auto-converts cents ↔ decimal
  useGrouping: true,
  hideGroupingSeparatorOnFocus: true,
  hideCurrencySymbolOnFocus: true,
  hideNegligibleDecimalDigitsOnFocus: true,
  autoDecimalDigits: false,


  accountingSign: false,

};


/**
 * Composable for currency formatting (SSOT)
 * Replaces all manual formatCurrency functions
 */
export function useCurrency() {
  /**
     * Format cents to display string
     * @param {number} cents - Value in cents (integer)
     * @returns {string} Formatted currency string
     */
  const formatCurrency = (cents) => {
    if (cents == null || isNaN(cents)) return '0.00 ج.م';

    const formatter = new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Convert cents to decimal for formatting
    return formatter.format(cents / 100);
  };

  /**
     * Format cents to a bare grouped number, NO currency symbol.
     * For rows that already carry a currency suffix elsewhere (or several
     * numbers on one compact line, where repeating the symbol is noise).
     * `trimZeros` drops a ".00" on a whole-pound amount ("2,480" but still
     * "2,480.50") — the collector's staff screens print market prices as the
     * paper does.
     * @param {number} cents - Value in cents (integer)
     * @param {{trimZeros?: boolean}} [options]
     * @returns {string} Formatted bare number string, e.g. "1,425.00"
     */
  const formatAmount = (cents, { trimZeros = false } = {}) => {
    if (cents == null || isNaN(cents)) return trimZeros ? '0' : '0.00';

    const whole = trimZeros && Number(cents) % 100 === 0;
    const formatter = new Intl.NumberFormat('ar-AE', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(cents / 100);
  };

  /**
     * Format cents to a SIGNED currency string ("+83.20 ج.م." / "-83.20 ج.م.").
     *
     * Use this instead of prefixing a sign onto formatCurrency() output. That
     * output is wrapped in RLM marks, so a sign glued on outside them is a
     * neutral character in an RTL line: the browser pushes it to the visual
     * end and it renders detached from its number ("83.20 ج.م.−"). Intl emits
     * the sign inside its own LRM, which keeps it on the digits.
     * @param {number} cents - Value in cents (integer)
     * @returns {string} Signed currency string; unsigned when zero
     */
  const formatSignedCurrency = (cents) => {
    if (cents == null || isNaN(cents)) return '0.00 ج.م';

    const formatter = new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'exceptZero',
    });

    return formatter.format(cents / 100);
  };

  /**
     * Parse display string to cents
     * @param {string} str - Formatted currency string
     * @returns {number} Value in cents
     */
  const parseCurrency = (str) => {
    if (!str) return 0;

    // Remove currency symbols and whitespace
    const cleanStr = str.replace(/[^\d.,٫-]/g, '');

    // Normalize decimal separator
    const normalized = cleanStr.replace(/[,٫]/g, '.');

    // Parse as float and convert to cents
    const decimal = parseFloat(normalized) || 0;
    return Math.round(decimal * 100);
  };

  /**
     * Create a currency input binding
     * @param {number} autoEmit - Whether to auto-emit on input
     * @returns {object} Currency input composable result
     */
  const createCurrencyInput = (autoEmit = true) => {
    return useCurrencyInput(EGP_CURRENCY_OPTIONS, autoEmit);
  };

  return {
    formatCurrency,
    formatAmount,
    formatSignedCurrency,
    parseCurrency,
    createCurrencyInput,
    currencyOptions: EGP_CURRENCY_OPTIONS,
  };
}
